/**
 * The Dockerfiles and Docker Compose files found in a project's services (vfs-service `GET /runnables`) and what the
 * people of the project chose for each, for the Apps tab ("Found in this service"):
 *
 * - a found file is shown on its service's page, pinned (it becomes an item of the Apps list, started as a session of
 *   its own) or ignored (in a collapsed "Ignored (n)" list); the choices are saved in the project, `.flow/project.json`
 *   `runnables: [{ path, kind, state: 'pinned' | 'ignored' }]` (`path` relative to the project), so every collaborator
 *   sees the same and they travel with the code;
 * - a Dockerfile that builds the service itself (`own`) offers How it runs: Build from code | Use the Dockerfile, saved
 *   in the service's `.flow/service.json` (`run: 'docker'` + `dockerfile`; back to code removes them);
 * - a pinned file runs as the runner's session `<service>--<kind>-<slug of its path>` (container-live/preview-runner
 *   `crates/core/src/runnable.rs`, the same name built here), next to the service's own run.
 */

export const RUNNABLE_KINDS = ['compose', 'dockerfile'];
export const RUNNABLES_KEY = 'runnables';
export const STATES = ['pinned', 'ignored'];

/** Why a pinned file cannot run where Docker is off (the runner's own wording: the plan's "Docker in the sandbox"). */
export const DOCKER_PLAN_MESSAGE = 'Running it needs "Docker in the sandbox", which your plan does not include.';
/** Where this Flow does not run containers at all (vfs-service `dockerOff: 'cluster'`): no plan change helps. */
export const DOCKER_CLUSTER_MESSAGE = 'Docker isn\'t available in this Flow yet. Ask your Flow admin.';
/** Where the sandbox type the person picked runs without Docker (vfs-service `dockerOff: 'type'`): another type helps. */
export const DOCKER_TYPE_MESSAGE = 'Running it needs Docker, which your sandbox type runs without. Pick another type in Settings › Sandbox › Sandbox type.';

/**
 * Why Docker is off for a project's pod, from vfs-service `GET /runnables` `dockerOff`: `'cluster'` (this Flow has no
 * Docker support: the sandbox gateway's containers piece is off or the cluster cannot run it), `'plan'` (the plan does
 * not include "Docker in the sandbox"), `'type'` (the sandbox type the person picked runs without it), null (on, or an older vfs-service: read as the plan, as before). Remembered per
 * project by getRunnables so the pickers that show the message read it without a prop through every page.
 */
export const dockerOffOf = (value) => (value === 'cluster' || value === 'plan' || value === 'type' ? value : null);
const dockerOffByProject = new Map();
export function rememberDockerOff(projectId, reason) {
  if (projectId) { dockerOffByProject.set(projectId, dockerOffOf(reason)); }
}
export const rememberedDockerOff = (projectId) => dockerOffByProject.get(projectId) || null;
/** The sentence for a run that needs Docker while it is off; only the plan's comes with Change plan (noDockerPlanChange). */
export const dockerOffMessage = (reason) => (reason === 'cluster' ? DOCKER_CLUSTER_MESSAGE : reason === 'type' ? DOCKER_TYPE_MESSAGE : DOCKER_PLAN_MESSAGE);
export const noDockerPlanChange = (reason) => reason !== 'cluster' && reason !== 'type';

/** `compose-dev-docker-compose-yml`: the kind and the file (relative to the service), lowercase, other characters one `-`. */
export function runnableSlug(kind, file) {
  const slug = String(file || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return `${kind}-${slug}`;
}

/** The runner's service name of a pinned file: `services/web--compose-dev-docker-compose-yml`. */
export const runnableServiceName = (service, { kind, file }) => `${String(service).replace(/\/+$/, '')}--${runnableSlug(kind, file)}`;

/** `{ service, kind }` of a runner service name that is a pinned file's, else null. */
export function parseRunnableName(name) {
  const match = String(name || '').match(/^(.+)--(compose|dockerfile)-[a-z0-9]+(?:-[a-z0-9]+)*$/);
  return match ? { service: match[1], kind: match[2] } : null;
}

/** A compose file's preview entry point as saved (`{ service, port }`, the runner's EntryPoint), or null when not valid. */
export function entryOf(value) {
  const service = typeof value?.service === 'string' ? value.service.trim() : '';
  const port = Number(value?.port);
  return service && Number.isInteger(port) && port > 0 && port < 65536 ? { service, port } : null;
}

/**
 * The choices saved in `.flow/project.json` (a parsed object), each `{ path, kind, state, entry? }` (`entry` a pinned
 * compose file's preview entry point); [] when none or not valid.
 */
export function choicesOf(settings) {
  const list = Array.isArray(settings?.[RUNNABLES_KEY]) ? settings[RUNNABLES_KEY] : [];
  return list.filter((item) => item && typeof item.path === 'string' && RUNNABLE_KINDS.includes(item.kind) && STATES.includes(item.state))
    .map(({ path, kind, state, entry }) => (entryOf(entry) ? { path, kind, state, entry: entryOf(entry) } : { path, kind, state }));
}

/** The choices with `path` set to `state` (null: no choice any more, it shows as found again); its `entry` kept. */
export function withChoice(choices, { path, kind, state }) {
  const before = (choices || []).find((item) => item.path === path);
  const rest = (choices || []).filter((item) => item.path !== path);
  if (!state) { return rest; }
  return [...rest, before?.entry && state === 'pinned' ? { path, kind, state, entry: before.entry } : { path, kind, state }];
}

/** The choices with the pinned file at `path` given `entry` (`{ service, port }`; null removes it). */
export function withEntry(choices, path, entry) {
  const valid = entryOf(entry);
  return (choices || []).map((item) => {
    if (item.path !== path) { return item; }
    const rest = { path: item.path, kind: item.kind, state: item.state };
    return valid ? { ...rest, entry: valid } : rest;
  });
}

/** The preview entry point saved for a pinned file, or null. */
export const savedEntryOf = (choices, path) => (choices || []).find((item) => item.path === path)?.entry || null;

/** The state chosen for a found file: 'pinned', 'ignored' or null. */
export const stateOf = (choices, path) => (choices || []).find((item) => item.path === path)?.state || null;

/** A service's found files split for its page: `{ shown: [{ ...item, state }], ignored: [item] }`. */
export function splitFound(runnables, choices) {
  const shown = [];
  const ignored = [];
  (runnables || []).forEach((item) => {
    const state = stateOf(choices, item.path);
    if (state === 'ignored') { ignored.push(item); } else { shown.push({ ...item, state }); }
  });
  return { shown, ignored };
}

/** The Dockerfiles that build the service itself (vfs-service's `own`), for "How it runs". */
export const ownDockerfiles = (runnables) => (runnables || []).filter((item) => item.kind === 'dockerfile' && item.own);

/**
 * The pinned files that are still in the project, as Apps list items, in the order of their services (not the file a
 * service runs through by itself, `runsService`: that is the service's own entry):
 * `[{ serviceName, service, kind, file, path, services, problems }]` (`serviceName` the runner's name, `services` a
 * compose file's services, `problems` what vfs-service found wrong in it (blocks a start), `warnings` what it found worth a look; [] when
 * nothing or not said).
 */
export function pinnedItems(scan, choices) {
  const items = [];
  Object.entries(scan?.services || {}).sort(([a], [b]) => a.localeCompare(b)).forEach(([service, found]) => {
    (found?.runnables || []).forEach((item) => {
      if (stateOf(choices, item.path) !== 'pinned') { return; }
      // The file the service itself runs through (vfs-service `runsService`: a folder with only a compose file or a
      // Dockerfile): the service is that app, one entry and one session; an older pin of it is not a second one.
      if (item.runsService) { return; }
      items.push({
        serviceName: runnableServiceName(service, item), service, kind: item.kind, file: item.file, path: item.path, services: item.services || [],
        // What vfs-service found wrong in the file before a start (a mounted file not in the project), when it says.
        problems: Array.isArray(item.problems) ? item.problems : [],
        warnings: Array.isArray(item.warnings) ? item.warnings : [],
        // Each missing bind-mount source (what can provide it) and each one a file secret or a kept folder stands for.
        missing: Array.isArray(item.missing) ? item.missing : [],
        provided: Array.isArray(item.provided) ? item.provided : [],
        // A compose file's saved preview entry point (`{ service, port }`), sent with its start (runnable.entry).
        ...(savedEntryOf(choices, item.path) ? { entry: savedEntryOf(choices, item.path) } : {}),
      });
    });
  });
  return items;
}

/**
 * The compose services (`['mongo', 'crud-service']`) of what runs a service, from vfs-service's scan: the compose file
 * whose session `sessionName` is (a pinned file's), else the one the service runs through by itself (`runsService`).
 * [] when it is not a compose app or the scan does not say.
 */
export function composeServicesOf(scan, serviceName, sessionName = null) {
  const found = (scan?.services?.[serviceName]?.runnables || []).filter((item) => item.kind === 'compose');
  const item = (sessionName && found.find((entry) => runnableServiceName(serviceName, entry) === sessionName)) || found.find((entry) => entry.runsService);
  return (item?.services || []).map((entry) => (typeof entry === 'string' ? entry : entry?.name)).filter(Boolean);
}

/** How the service runs now, from its `.flow/service.json` (parsed): `'docker'` with the Dockerfile, or `'code'`. */
export function howItRunsChoice(spec) {
  return spec?.run === 'docker' ? { how: 'docker', dockerfile: spec.dockerfile || 'Dockerfile' } : { how: 'code', dockerfile: null };
}

/**
 * The service.json after choosing how it runs: `{ run: 'docker', dockerfile }` added, or (back to code) both removed;
 * null when nothing is left (the file goes away: the runner detects the stack as before).
 */
export function specWithChoice(spec, how, dockerfile = null) {
  const next = { ...(spec || {}) };
  if (how === 'docker') {
    next.run = 'docker';
    next.dockerfile = dockerfile || 'Dockerfile';
  } else {
    delete next.run;
    delete next.dockerfile;
    delete next.context;
  }
  return Object.keys(next).filter((key) => key !== '$comment').length ? next : null;
}

/** Why the service cannot switch to its Dockerfile, or null: the runner refuses `run: 'docker'` next to `start`. */
export function dockerChoiceBlocked(spec) {
  if (spec?.run === 'compose') { return 'It runs as a Docker Compose app (run: "compose" in .flow/service.json).'; }
  if (spec?.start) { return 'It starts with its own command (start in .flow/service.json): remove it to use the Dockerfile.'; }
  return null;
}
